import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { ArrowLeft, Save, Trash2, FileImage, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { k: "full_name", label: "Full Name" },
  { k: "email", label: "Email", type: "email" },
  { k: "phone", label: "Phone" },
  { k: "dob", label: "Date of Birth", placeholder: "MM/DD/YYYY" },
  { k: "drivers_license", label: "Driver's License #" },
  { k: "license_state", label: "License State" },
  { k: "license_expiry", label: "License Expiry", placeholder: "MM/DD/YYYY" },
  { k: "ssn_last4", label: "SSN (last 4)" },
  { k: "insurance_provider", label: "Insurance Provider" },
  { k: "insurance_policy", label: "Insurance Policy #" },
  { k: "employer", label: "Employer" },
  { k: "monthly_income", label: "Monthly Income ($)", type: "number" },
  { k: "housing_status", label: "Housing Status" },
  { k: "time_at_residence", label: "Time at Residence" },
  { k: "employment_duration", label: "Employment Duration" },
];

export default function AdminCustomerDetail() {
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]);
  const [preview, setPreview] = useState(null); // { doc, dataUrl }
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    api.get(`/customers/${id}`).then((r) => setCustomer(r.data));
    api.get(`/documents?customer_id=${id}`).then((r) => setDocs(r.data || []));
  }, [id]);

  const change = (k) => (e) =>
    setCustomer({ ...customer, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/customers/${id}`, customer);
      setCustomer(data);
      toast.success("Customer updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openDoc = async (doc) => {
    setPreview({ doc, dataUrl: "" });
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/documents/${doc.id}`);
      setPreview({ doc, dataUrl: data.content_base64 });
    } catch {
      toast.error("Could not open document");
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Soft-delete this document? Staff can no longer view it.")) return;
    try {
      await api.delete(`/documents/${docId}`);
      setDocs((d) => d.filter((x) => x.id !== docId));
      toast.success("Document deleted");
      if (preview?.doc?.id === docId) setPreview(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!customer) return <AdminShell title="Customer"><div className="text-slate-400">Loading…</div></AdminShell>;

  const billingSame =
    !customer.billing_address && !customer.billing_city && !customer.billing_state && !customer.billing_zip;

  return (
    <AdminShell title={customer.full_name || "Customer"}>
      <Link to="/admin/customers" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3 h-3" /> Back to customers
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Profile edit — 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          <div className="surface rounded-md p-6" data-testid="customer-profile-form">
            <div className="flex items-center justify-between mb-4">
              <div className="label-eyebrow" style={{ color: "var(--wakzy-gold)" }}>
                Profile
              </div>
              <Button
                onClick={save}
                disabled={saving}
                className="btn-cta rounded-sm h-9 px-4 text-xs"
                data-testid="customer-save-btn"
              >
                {saving ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Save className="w-3 h-3 mr-2" />}
                Save
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {FIELDS.map((f) => (
                <div key={f.k}>
                  <Label className="text-xs text-slate-400">{f.label}</Label>
                  <Input
                    type={f.type || "text"}
                    placeholder={f.placeholder || ""}
                    value={customer[f.k] ?? ""}
                    onChange={change(f.k)}
                    className="bg-[#04060d] border-white/10 h-10 mt-1"
                    data-testid={`customer-${f.k}-input`}
                  />
                </div>
              ))}
            </div>

            {/* Residential */}
            <div className="mt-6 border-t border-white/5 pt-4">
              <div className="label-eyebrow mb-3">Residential Address</div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Street" v={customer.address} on={change("address")} testid="customer-address-input" />
                <div className="grid grid-cols-3 gap-2">
                  <Field label="City" v={customer.city} on={change("city")} testid="customer-city-input" />
                  <Field label="State" v={customer.state} on={change("state")} testid="customer-state-input" />
                  <Field label="Zip" v={customer.zip} on={change("zip")} testid="customer-zip-input" />
                </div>
              </div>
            </div>

            {/* Billing */}
            <div className="mt-6 border-t border-white/5 pt-4">
              <div className="label-eyebrow mb-3">
                Billing Address
                {billingSame && <span className="text-slate-500 normal-case font-normal ml-2">(same as residential)</span>}
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Field label="Street" v={customer.billing_address} on={change("billing_address")} testid="customer-billing-address-input" />
                <div className="grid grid-cols-3 gap-2">
                  <Field label="City" v={customer.billing_city} on={change("billing_city")} testid="customer-billing-city-input" />
                  <Field label="State" v={customer.billing_state} on={change("billing_state")} testid="customer-billing-state-input" />
                  <Field label="Zip" v={customer.billing_zip} on={change("billing_zip")} testid="customer-billing-zip-input" />
                </div>
              </div>
              <p className="text-[10px] text-slate-500 mt-2">Leave blank to mirror residential address on invoices.</p>
            </div>
          </div>
        </div>

        {/* Documents column */}
        <div className="space-y-4">
          <div className="surface rounded-md p-4" data-testid="customer-documents-panel">
            <div className="label-eyebrow mb-3" style={{ color: "var(--wakzy-gold)" }}>
              Collected Documents ({docs.length})
            </div>
            {docs.length === 0 && <div className="text-xs text-slate-500">No documents on file yet.</div>}
            <ul className="space-y-2">
              {docs.map((d) => (
                <li
                  key={d.id}
                  className="border border-white/5 rounded-sm p-2 hover:bg-white/[0.03] transition"
                  data-testid={`doc-row-${d.id}`}
                >
                  <div className="flex items-start gap-2">
                    <FileImage className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-white truncate">{d.filename}</div>
                      <div className="text-[10px] text-slate-500">
                        {d.doc_type} · {(d.size / 1024).toFixed(1)} KB · {d.created_at?.slice(0, 10)}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => openDoc(d)}
                      className="text-[10px] text-[#D4AF37] hover:underline inline-flex items-center gap-1"
                      data-testid={`doc-open-${d.id}`}
                    >
                      <ExternalLink className="w-3 h-3" /> View
                    </button>
                    <button
                      onClick={() => deleteDoc(d.id)}
                      className="text-[10px] text-red-400 hover:underline inline-flex items-center gap-1 ml-auto"
                      data-testid={`doc-delete-${d.id}`}
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Preview modal */}
      {preview && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6"
          onClick={() => setPreview(null)}
          data-testid="doc-preview-overlay"
        >
          <div
            className="surface rounded-md p-4 max-w-3xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="text-xs text-slate-400 truncate">{preview.doc.filename}</div>
              <button
                onClick={() => setPreview(null)}
                className="text-xs text-slate-400 hover:text-white"
                data-testid="doc-preview-close"
              >
                Close
              </button>
            </div>
            {previewLoading ? (
              <div className="text-slate-400 text-sm py-12 text-center">Loading…</div>
            ) : (
              <img src={preview.dataUrl} alt={preview.doc.filename} className="max-w-full min-w-[280px] min-h-[280px] object-contain rounded-sm mx-auto" />
            )}
          </div>
        </div>
      )}
    </AdminShell>
  );
}

function Field({ label, v, on, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <Input
        value={v ?? ""}
        onChange={on}
        className="bg-[#04060d] border-white/10 h-10 mt-1"
        data-testid={testid}
      />
    </div>
  );
}
